import React, { useState } from 'react';
import Icon from '../AppIcon';
import { Checkbox } from './Checkbox';
import Button from './Button';

const HeritageMapFilter = ({ 
  isOpen = false, 
  onToggle, 
  onFilterChange, 
  activeFilters = { 
    cultural: true, 
    natural: true, 
    showAll: true,
    trails: true,
    montenegroTrails: true,
    serbiaTrails: true
  }
}) => {
  const [filters, setFilters] = useState(activeFilters);

  const filterOptions = [
    {
      id: 'cultural',
      label: 'Cultural Heritage Sites',
      description: 'Monasteries, fortresses, and historical landmarks',
      icon: 'Building2',
      count: 48,
      color: 'text-accent',
      category: 'heritage'
    },
    {
      id: 'natural',
      label: 'Natural Heritage Sites',
      description: 'National parks, mountains, and scenic viewpoints',
      icon: 'Trees',
      count: 38,
      color: 'text-success',
      category: 'heritage'
    },
    {
      id: 'showAll',
      label: 'Show All Heritage Sites',
      description: 'Display all heritage sites on the map',
      icon: 'MapPin',
      count: 86,
      color: 'text-primary',
      category: 'heritage'
    }
  ];

  const trailOptions = [
    {
      id: 'trails',
      label: 'Show Thematic Trails',
      description: 'Display trail routes connecting heritage sites',
      icon: 'Route',
      count: 8,
      color: 'text-blue-600',
      category: 'trails'
    },
    {
      id: 'montenegroTrails',
      label: 'Montenegro Trails',
      description: '4 thematic trails in Montenegro (Orange routes)',
      icon: 'Mountain',
      count: 4,
      color: 'text-orange-600',
      category: 'trails'
    },
    {
      id: 'serbiaTrails',
      label: 'Serbia Trails',
      description: '4 thematic trails in Serbia (Green routes)',
      icon: 'TreePine',
      count: 4,
      color: 'text-green-700',
      category: 'trails'
    }
  ];

  const handleFilterChange = (filterId, checked) => {
    const newFilters = { ...filters, [filterId]: checked };
    
    // Handle trail dependencies
    if (filterId === 'trails' && !checked) {
      newFilters.montenegroTrails = false;
      newFilters.serbiaTrails = false;
    } else if ((filterId === 'montenegroTrails' || filterId === 'serbiaTrails') && checked) {
      newFilters.trails = true;
    }
    
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = { 
      cultural: false, 
      natural: false, 
      showAll: false,
      trails: false,
      montenegroTrails: false,
      serbiaTrails: false
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const selectAllFilters = () => {
    const allFilters = { 
      cultural: true, 
      natural: true, 
      showAll: true,
      trails: true,
      montenegroTrails: true,
      serbiaTrails: true
    };
    setFilters(allFilters);
    onFilterChange(allFilters);
  };

  return (
    <>
      {/* Mobile Filter Toggle Button with improved z-index */}
      <div className="lg:hidden fixed top-20 left-4 z-[1002]">
        <Button
          variant="default"
          size="sm"
          onClick={onToggle}
          className="shadow-lg"
        >
          <Icon name="Filter" size={16} />
          <span className="ml-2">Filters</span>
        </Button>
      </div>

      {/* Desktop Sidebar - Remove fixed positioning as it's handled by parent */}
      <div className="hidden lg:block">
        <FilterContent
          filterOptions={filterOptions}
          trailOptions={trailOptions}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          onSelectAll={selectAllFilters}
        />
      </div>

      {/* Mobile Overlay with improved z-index */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-[1010]">
          <div className="absolute inset-0 bg-black/50" onClick={onToggle} />
          <div className="absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-card shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-heading font-semibold text-lg">Map Filters</h2>
              <Button variant="ghost" size="icon" onClick={onToggle}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="p-6 h-[calc(100%-5rem)] overflow-y-auto">
              <FilterContent
                filterOptions={filterOptions}
                trailOptions={trailOptions}
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearAll={clearAllFilters}
                onSelectAll={selectAllFilters}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const FilterContent = ({ filterOptions, trailOptions, filters, onFilterChange, onClearAll, onSelectAll }) => {
  const allOptions = [...filterOptions, ...trailOptions];
  const activeCount = Object.values(filters)?.filter(Boolean)?.length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading font-semibold text-base mb-2">Interactive Map Controls</h3>
        <p className="text-muted-foreground text-sm mb-4">
          Choose heritage sites and thematic trails to display on the map
        </p>
      </div>
      
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onSelectAll}
          className="flex-1"
        >
          Select All
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onClearAll}
          className="flex-1"
        >
          Clear All
        </Button>
      </div>

      {/* Heritage Sites Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="MapPin" size={16} className="text-primary" />
          <h4 className="font-heading font-medium text-sm">Heritage Sites</h4>
        </div>
        {filterOptions?.map((option) => (
          <div key={option?.id} className="space-y-2">
            <Checkbox
              checked={filters?.[option?.id]}
              onChange={(e) => onFilterChange(option?.id, e?.target?.checked)}
              label={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <Icon name={option?.icon} size={20} className={option?.color} />
                    <div>
                      <span className="font-medium text-sm">{option?.label}</span>
                    </div>
                  </div>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {option?.count}
                  </span>
                </div>
              }
              description={option?.description}
            />
          </div>
        ))}
      </div>

      {/* Trails Section */}
      <div className="space-y-4 border-t border-border pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Route" size={16} className="text-blue-600" />
          <h4 className="font-heading font-medium text-sm">Thematic Trails</h4>
        </div>
        {trailOptions?.map((option) => (
          <div key={option?.id} className="space-y-2">
            <Checkbox
              checked={filters?.[option?.id]}
              onChange={(e) => onFilterChange(option?.id, e?.target?.checked)}
              disabled={option?.id !== 'trails' && !filters?.trails}
              label={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <Icon name={option?.icon} size={20} className={option?.color} />
                    <div>
                      <span className="font-medium text-sm">{option?.label}</span>
                    </div>
                  </div>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {option?.count}
                  </span>
                </div>
              }
              description={option?.description}
            />
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Active filters:</span>
          <span className="font-medium text-foreground">{activeCount} of {allOptions?.length}</span>
        </div>
      </div>
    </div>
  );
};

export default HeritageMapFilter;